/**
 * 異常検知カードの判断（F-16・`docs/02` §9.2）。★計算はしない。サーバの判定を言葉へ写すだけ。
 *
 * ★指標名・当日値・中央値・比較件数は「警告」のときだけ、指標ごとに1行で出す。
 * ★★**「正常」も1行で言う**（依頼者判断 2026-09-21・通し検証）。黙っていると、
 *   毎日並ぶ「判定不能」だけが目に入り、異常なしの日に「異常なし」と読めなかった。
 *   ★材料が無い（判定不能）を「普段どおり」に畳まない——正常の行に入るのは `normal` だけ。
 * ★「判定不能」は**同じ理由の指標を1行にまとめる**（原価率・人件費率は毎日同じ理由で並ぶため）。
 */
import type { AnomalyMetricKey, DailyAnomalies } from "./api.js";

export interface AnomalyLine {
  metrics: AnomalyMetricKey[];
  /** 「警告」は `.warn`（誤りではない）／「正常」は一致の色／「判定不能」は理由だけ小さく（`.muted`） */
  kind: "anomaly" | "normal" | "indeterminate";
  text: string;
}

export interface AnomalyCardView {
  lines: AnomalyLine[];
}

const METRIC_ORDER: readonly AnomalyMetricKey[] = ["sales_excl_tax", "guests", "cost_rate", "labor_cost_rate"];

const METRIC_LABEL: Record<AnomalyMetricKey, string> = {
  sales_excl_tax: "売上（税抜）",
  guests: "客数",
  cost_rate: "原価率",
  labor_cost_rate: "人件費率",
};

/** ★率(0〜1)以外は amount / guests 系の整数として言葉にする */
function formatMetricValue(metric: AnomalyMetricKey, n: number): string {
  if (metric === "sales_excl_tax") return `${n.toLocaleString()}円`;
  if (metric === "guests") return `${n.toLocaleString()}人`;
  return `${(n * 100).toFixed(1)}%`;
}

export function anomalyView(anomalies: DailyAnomalies): AnomalyCardView {
  const judged = METRIC_ORDER.map((metric) => ({ metric, j: anomalies.metrics[metric] }));
  const lines: AnomalyLine[] = [];
  for (const { metric, j } of judged) {
    if (j.status !== "anomaly") continue;
    lines.push({
      metrics: [metric],
      kind: "anomaly",
      text:
        `${METRIC_LABEL[metric]}：普段と大きく違います（当日 ${formatMetricValue(metric, j.value)}／` +
        `目安 ${formatMetricValue(metric, j.median)}／比較${String(j.sampleCount)}件）`,
    });
  }
  const normal = judged.filter(({ j }) => j.status === "normal");
  if (normal.length > 0) {
    const fewest = Math.min(...normal.map(({ j }) => j.sampleCount));
    lines.push({
      metrics: normal.map(({ metric }) => metric),
      kind: "normal",
      text: `✓ ${normal.map(({ metric }) => METRIC_LABEL[metric]).join("・")}：普段どおり（同じ曜日と比較${String(fewest)}件）`,
    });
  }
  const byReason = new Map<string, AnomalyMetricKey[]>();
  for (const { metric, j } of judged) {
    if (j.status === "indeterminate") byReason.set(j.reason, [...(byReason.get(j.reason) ?? []), metric]);
  }
  for (const [reason, metrics] of byReason) {
    lines.push({ metrics, kind: "indeterminate", text: `${metrics.map((m) => METRIC_LABEL[m]).join("・")}：${reason}` });
  }
  return { lines };
}
